import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import ComboBoxSearch from "@/components/ComboBoxSearch";
import useProductionUnit from "@/hooks/useProductionUnit";
import { Search, Plus, Save, X, Factory, ArrowLeft, CheckCircle2, Ban, Eye, Printer } from "lucide-react";
import { toast } from "sonner";

import { formatDate, formatDateTime } from "@/lib/datetime";
const STATUS_TONE = {
  on_process: "bg-amber-50 text-amber-800 border-amber-200",
  partial: "bg-blue-50 text-blue-800 border-blue-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-red-50 text-red-800 border-red-200",
};

export default function Fabrication() {
  const [view, setView] = useState({ mode: "list" });    // {mode:"list"} | {mode:"new"} | {mode:"edit",id}
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [workerId, setWorkerId] = useState("");
  const [productionUnitId, setProductionUnitId] = useState("");
  const [workerOpts, setWorkerOpts] = useState([]);
  const [unitOpts, setUnitOpts] = useState([]);
  const [receiveFor, setReceiveFor] = useState(null);

  useEffect(() => {
    api.get("/production/workers").then((r) => setWorkerOpts(r.data || [])).catch(() => {});
    api.get("/production/units").then((r) => setUnitOpts((r.data || []).filter((u) => u.is_active !== false))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const params = { page: 1, page_size: 100 };
      if (status) params.status = status;
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (workerId) params.worker_id = workerId;
      if (productionUnitId) params.production_unit_id = productionUnitId;
      const r = await api.get("/production/fabrications", { params });
      setItems(r.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [status, search, dateFrom, dateTo, workerId, productionUnitId]);
  useEffect(() => { if (view.mode === "list") load(); }, [view.mode, load]);

  if (view.mode === "new" || view.mode === "edit") {
    return <FabricationEditor batchId={view.id} onBack={() => setView({ mode: "list" })} />;
  }

  return (
    <div className="space-y-4">
      <PageSection title="Level 1 — Fabrication" description="Issue raw materials → produce semi-finished pieces" action={
        <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={() => setView({ mode: "new" })} data-testid="fab-new-btn">
          <Plus className="h-4 w-4" /> New L1 Batch
        </Button>
      }>
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 max-w-xs min-w-[200px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search batch # / product..." className="pl-8" data-testid="fab-search" />
            </div>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" title="From date" data-testid="fab-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" title="To date" data-testid="fab-date-to" />
            <select value={workerId} onChange={(e) => setWorkerId(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="fab-worker-filter">
              <option value="">All workers</option>
              {workerOpts.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            {unitOpts.length > 1 && (
              <select value={productionUnitId} onChange={(e) => setProductionUnitId(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="fab-unit-filter">
                <option value="">All units</option>
                {unitOpts.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            )}
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="fab-status-filter">
              <option value="">All statuses</option>
              <option value="on_process">On Process</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            {(dateFrom || dateTo || workerId || productionUnitId || status || search) && (
              <button onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setWorkerId(""); setProductionUnitId(""); setStatus(""); }} className="text-[11px] text-gray-500 hover:text-gray-900 underline">Clear</button>
            )}
            <span className="text-xs text-gray-500 ml-auto">{items.length} batches</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Batch #</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Raw Materials</th>
                <th className="px-3 py-2 text-right">Issued</th>
                <th className="px-3 py-2 text-right">Received</th>
                <th className="px-3 py-2 text-right">Pending</th>
                <th className="px-3 py-2">Unit</th>
                <th className="px-3 py-2">Worker</th>
                <th className="px-3 py-2 text-right">Payable</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-gray-400">
                  <Factory className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  No L1 batches yet.
                </td></tr>
              )}
              {items.map((b) => {
                const issuedTotal = (b.rm_lines || []).reduce((s, l) => s + Number(l.issued_qty || 0), 0);
                const receivedTotal = Number(b.received_qty || 0);
                const pending = Math.max(0, issuedTotal - receivedTotal);
                const canReceive = ["on_process", "partial"].includes(b.status);
                return (
                <tr key={b.id} className="hover:bg-gray-50" data-testid={`fab-row-${b.id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-[#9B1C1C]">{b.batch_no}</td>
                  <td className="px-3 py-2 text-gray-600">{formatDate(b.production_date)}</td>
                  <td className="px-3 py-2">
                    <div className="text-xs">{(b.rm_lines || []).slice(0, 2).map((l) => l.raw_material_name).join(", ")}{(b.rm_lines || []).length > 2 ? ` +${b.rm_lines.length - 2}` : ""}</div>
                    {b.output_raw_material_name && <div className="text-[10px] text-emerald-700 font-semibold">→ {b.output_raw_material_name}</div>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{issuedTotal}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-emerald-700">{receivedTotal || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold">{pending > 0 ? pending : "—"}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{b.production_unit_name || "—"}</td>
                  <td className="px-3 py-2 text-gray-700 text-xs">{b.worker_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(b.worker_payable || 0)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${STATUS_TONE[b.status] || STATUS_TONE.cancelled}`}>
                      {b.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {b.status !== "cancelled" && (
                      <a className="h-7 w-7 rounded hover:bg-gray-100 inline-flex items-center justify-center mr-1" href={`/production/fabrications/${b.id}/issue-slip`} target="_blank" rel="noreferrer" title="Print Issue Slip" data-testid={`fab-slip-${b.id}`}>
                        <Printer className="h-3.5 w-3.5 text-gray-500" />
                      </a>
                    )}
                    {canReceive && (
                      <>
                        <button onClick={() => setReceiveFor(b)} className="h-7 px-2 text-xs rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold border border-emerald-200 mr-1" data-testid={`fab-receive-${b.id}`}>
                          Receive
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm(`Cancel batch ${b.batch_no}? RM will be returned to stock.`)) return;
                          try { await api.post(`/production/fabrications/${b.id}/cancel`); toast.success("Cancelled"); load(); }
                          catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
                        }} className="h-7 w-7 rounded hover:bg-red-50 inline-flex items-center justify-center" data-testid={`fab-cancel-${b.id}`} title="Cancel batch">
                          <Ban className="h-3.5 w-3.5 text-red-500" />
                        </button>
                      </>
                    )}
                    <button onClick={() => setView({ mode: "edit", id: b.id })} className="h-7 w-7 rounded hover:bg-gray-100 inline-flex items-center justify-center ml-1" data-testid={`fab-view-${b.id}`}>
                      <Eye className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </Surface>
      </PageSection>

      {receiveFor && <ReceiveDialog batch={receiveFor} onClose={() => setReceiveFor(null)} onDone={() => { setReceiveFor(null); load(); }} />}
    </div>
  );
}

// ------------------ Editor ------------------

function FabricationEditor({ batchId, onBack }) {
  const isNew = !batchId;
  const [batch, setBatch] = useState(null);
  const [workerOptions, setWorkerOptions] = useState([]);
  const [rmStockMap, setRmStockMap] = useState({});
  const [rmOptions, setRmOptions] = useState([]);
  const { units: unitOptions, unitId, setUnitId, required: unitRequired } = useProductionUnit();
  const [form, setForm] = useState({
    production_date: new Date().toISOString().slice(0, 10),
    worker_id: null,
    notes: "",
    rm_lines: [{ raw_material_id: null, raw_material_name: "", unit: "", issued_qty: "" }],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/production/workers").then((r) => setWorkerOptions((r.data || []).map((w) => ({ value: w.id, label: w.name, sublabel: w.worker_type || "" })))); }, []);

  const searchRMs = useCallback(async (q) => {
    try {
      const r = await api.get("/production/raw-materials", { params: { search: q || "", page: 1, page_size: 30 } });
      setRmOptions((r.data.items || []).map((rm) => ({ value: rm.id, label: rm.name, sublabel: rm.unit || "" })));
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { searchRMs(""); }, [searchRMs]);

  const loadRmStock = useCallback(async (ids) => {
    if (!ids?.length) return;
    try {
      const r = await api.get("/production/rm-availability", { params: { ids: ids.join(",") } });
      setRmStockMap((prev) => ({ ...prev, ...(r.data || {}) }));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!batchId) return;
    (async () => {
      try {
        const r = await api.get(`/production/fabrications/${batchId}`);
        setBatch(r.data);
        setForm({
          production_date: r.data.production_date,
          worker_id: r.data.worker_id,
          notes: r.data.notes || "",
          rm_lines: (r.data.rm_lines || []).map((ln) => ({ ...ln, issued_qty: String(ln.issued_qty || "") })),
        });
        if (r.data.production_unit_id) setUnitId(r.data.production_unit_id);
        loadRmStock((r.data.rm_lines || []).map((ln) => ln.raw_material_id));
      } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); onBack(); }
    })();
  }, [batchId, onBack, loadRmStock]);

  const readOnly = !isNew;   // batches are immutable audit records once created

  const updateRmLine = (i, patch) => setForm((f) => {
    const rm_lines = f.rm_lines.map((l, idx) => idx === i ? { ...l, ...patch } : l);
    // Auto-append a fresh empty line when the LAST line just got a qty > 0 AND an RM
    const last = rm_lines[rm_lines.length - 1];
    if (last?.raw_material_id && Number(last.issued_qty) > 0) {
      rm_lines.push({ raw_material_id: null, raw_material_name: "", unit: "", issued_qty: "" });
    }
    return { ...f, rm_lines };
  });
  const removeRmLine = (i) => setForm((f) => ({ ...f, rm_lines: f.rm_lines.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.worker_id) return toast.error("Select a worker");
    if (unitRequired && !unitId) return toast.error("Select a Production Unit");
    const validLines = form.rm_lines.filter((l) => l.raw_material_id && Number(l.issued_qty) > 0);
    if (validLines.length === 0) return toast.error("Add at least one RM with qty");
    try {
      setSaving(true);
      await api.post("/production/fabrications", {
        production_date: form.production_date,
        rm_lines: validLines.map((l) => ({ raw_material_id: l.raw_material_id, issued_qty: Number(l.issued_qty) })),
        worker_id: form.worker_id,
        notes: form.notes || null,
        production_unit_id: unitId || null,
      });
      toast.success("L1 batch issued — RM stock debited");
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={onBack} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <h2 className="font-heading font-bold text-lg text-gray-900">{isNew ? "New L1 Fabrication Batch" : `Batch ${batch?.batch_no || ""}`}</h2>
        {batch?.status && <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${STATUS_TONE[batch.status] || "bg-gray-100 border-gray-200 text-gray-700"}`}>{batch.status.replace("_", " ")}</span>}
        {!isNew && batch?.status !== "cancelled" && (
          <a href={`/production/fabrications/${batchId}/issue-slip`} target="_blank" rel="noreferrer" className="ml-2">
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="fab-print-slip"><Printer className="h-3.5 w-3.5" /> Issue Slip</Button>
          </a>
        )}
        {!readOnly && <div className="ml-auto">
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={save} disabled={saving} data-testid="fab-save">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Issue Batch"}
          </Button>
        </div>}
      </div>

      <Surface>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-40">
            <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Production Date</label>
            <Input type="date" value={form.production_date} onChange={(e) => setForm({ ...form, production_date: e.target.value })} disabled={readOnly} data-testid="fab-date" />
          </div>
          {unitRequired && (
            <div className="w-64">
              <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Production Unit *</label>
              <ComboBoxSearch value={unitId} onChange={setUnitId} options={unitOptions} placeholder="Select production unit" testId="fab-unit" disabled={readOnly} />
            </div>
          )}
          <div className="w-64">
            <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Worker *</label>
            <ComboBoxSearch value={form.worker_id} onChange={(v) => setForm({ ...form, worker_id: v })} options={workerOptions} placeholder="Select worker" testId="fab-worker" disabled={readOnly} />
          </div>
          <div className="w-80">
            <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Notes</label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" disabled={readOnly} data-testid="fab-notes" />
          </div>
        </div>
      </Surface>

      <Surface padded={false}>
        <div className="p-3 border-b border-gray-200 flex items-center">
          <h3 className="font-heading font-semibold text-sm">Raw Materials to Issue</h3>
          <span className="ml-auto text-[11px] text-gray-500">A new empty line is added automatically once you enter a qty.</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
              <th className="px-3 py-2 w-10">#</th>
              <th className="px-3 py-2">Raw Material</th>
              <th className="px-3 py-2 text-center w-20">Unit</th>
              <th className="px-3 py-2 text-right w-32">Available</th>
              <th className="px-3 py-2 text-right w-32">Issue Qty</th>
              {readOnly && <th className="px-3 py-2 text-right w-32">Received</th>}
              {!readOnly && <th className="px-3 py-2 w-10"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {form.rm_lines.map((ln, i) => {
              const available = rmStockMap[ln.raw_material_id];
              const issue = Number(ln.issued_qty) || 0;
              const overIssue = available !== undefined && issue > available;
              return (
                <tr key={i}>
                  <td className="px-3 py-2 text-xs text-gray-400">{i + 1}</td>
                  <td className="px-3 py-2">
                    {readOnly ? <div className="font-medium">{ln.raw_material_name}</div> : (
                      <ComboBoxSearch
                        value={ln.raw_material_id}
                        onChange={(v) => {
                          const opt = rmOptions.find((o) => o.value === v);
                          updateRmLine(i, { raw_material_id: v, raw_material_name: opt?.label, unit: opt?.sublabel });
                          loadRmStock([v]);
                        }}
                        options={ln.raw_material_id && !rmOptions.find((o) => o.value === ln.raw_material_id)
                          ? [...rmOptions, { value: ln.raw_material_id, label: ln.raw_material_name, sublabel: ln.unit || "" }]
                          : rmOptions}
                        onSearchChange={searchRMs}
                        placeholder="Search RM..."
                        testId={`fab-line-${i}-rm`}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2 text-center text-xs text-gray-600">{ln.unit || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-700">{available === undefined ? "—" : available}</td>
                  <td className="px-3 py-2">
                    {readOnly ? <div className="text-right tabular-nums font-semibold">{ln.issued_qty}</div> : (
                      <Input type="number" step="0.001" value={ln.issued_qty} onChange={(e) => updateRmLine(i, { issued_qty: e.target.value })} className={`text-right tabular-nums ${overIssue ? "border-red-500 text-red-600" : ""}`} data-testid={`fab-line-${i}-qty`} />
                    )}
                    {overIssue && <div className="text-[10px] text-red-600 mt-0.5">Exceeds stock</div>}
                  </td>
                  {readOnly && (
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{ln.received_qty || 0}</td>
                  )}
                  {!readOnly && (
                    <td className="px-3 py-2 text-right">
                      {form.rm_lines.length > 1 && (
                        <button onClick={() => removeRmLine(i)} className="h-6 w-6 rounded hover:bg-red-50 inline-flex items-center justify-center"><X className="h-3 w-3 text-red-500" /></button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Surface>

      {batch && (batch.receipts_history || []).length > 0 && (
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-200 flex items-center">
            <h3 className="font-heading font-semibold text-sm">Receipt History</h3>
            <span className="ml-auto text-xs text-gray-500">Total received: <span className="font-semibold text-emerald-700 tabular-nums">{batch.received_qty}</span> · Worker payable: <span className="font-semibold text-[#9B1C1C]">{formatINR(batch.worker_payable || 0)}</span></span>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2 w-40">Date / Time</th>
                <th className="px-3 py-2">Received By</th>
                <th className="px-3 py-2">Items</th>
                <th className="px-3 py-2 text-right w-24">Payable</th>
                <th className="px-3 py-2">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(batch.receipts_history || []).map((h) => (
                <tr key={h.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 tabular-nums">{formatDateTime(h.received_at)}</td>
                  <td className="px-3 py-2">{h.received_by_name || h.received_by_email || "—"}</td>
                  <td className="px-3 py-2">{(h.lines || []).map((l) => `${l.raw_material_name}: ${l.received_qty}`).join(" · ")}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatINR(h.worker_payable || 0)}</td>
                  <td className="px-3 py-2 text-gray-600">{h.notes || "—"}{h.closed_here && <span className="ml-1 text-[10px] uppercase tracking-widest text-amber-700 font-semibold">· closed</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      )}
    </div>
  );
}

// ------------------ Receive Dialog ------------------

function ReceiveDialog({ batch, onClose, onDone }) {
  const lines = batch.rm_lines || [];
  const [rowInputs, setRowInputs] = useState({});   // { fab_line_id: {qty, output_rm_id, output_rm_name} }
  const [notes, setNotes] = useState("");
  const [closeBatch, setCloseBatch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [chargeMap, setChargeMap] = useState({});
  const [rmOptions, setRmOptions] = useState([]);
  const [rmSearch, setRmSearch] = useState("");

  // Preview worker charges — pulls per-RM rate (falls back to category default)
  useEffect(() => {
    const ids = lines.map((l) => l.raw_material_id).filter(Boolean);
    if (!ids.length) return;
    api.get("/production/rm-worker-charges", { params: { ids: ids.join(",") } })
      .then((r) => setChargeMap(r.data || {}))
      .catch(() => setChargeMap({}));
  }, [batch.id]);

  // Load raw-material list for the output picker (typeahead)
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/production/raw-materials", { params: { search: rmSearch || undefined, page: 1, page_size: 100 } })
        .then((r) => setRmOptions((r.data?.items || []).map((rm) => ({ value: rm.id, label: rm.name, sublabel: rm.unit || "" }))))
        .catch(() => setRmOptions([]));
    }, 200);
    return () => clearTimeout(t);
  }, [rmSearch]);

  const setLine = (id, patch) => setRowInputs((s) => ({ ...s, [id]: { ...(s[id] || {}), ...patch } }));

  const runningPayable = lines.reduce((acc, l) => {
    const q = Number(rowInputs[l.id]?.qty) || 0;
    const rate = Number(chargeMap[l.raw_material_id] || 0);
    return acc + q * rate;
  }, 0);

  const submit = async () => {
    const linesPayload = lines
      .map((l) => {
        const r = rowInputs[l.id] || {};
        return {
          fab_line_id: l.id,
          received_qty: Number(r.qty) || 0,
          output_raw_material_id: r.output_rm_id || l.raw_material_id,
        };
      })
      .filter((x) => x.received_qty > 0);
    if (linesPayload.length === 0 && !closeBatch) return toast.error("Enter at least one received qty (or tick Close batch)");
    try {
      setSaving(true);
      await api.post(`/production/fabrications/${batch.id}/receive`, {
        lines: linesPayload,
        receive_notes: notes || null,
        close_batch: closeBatch,
      });
      toast.success(`Batch ${batch.batch_no} — receipt recorded`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Receive failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl" data-testid="fab-receive-dialog">
        <DialogHeader>
          <DialogTitle>Receive Semi-Finished — {batch.batch_no}</DialogTitle>
          <DialogDescription>For each line, pick the <b>next-level RM</b> that stock will be credited to, then enter the received qty. E.g. issue &quot;Rg 1212 N Roll&quot; → pick &quot;Rg 24 1212 N&quot; on receive. Batch stays &quot;partial&quot; until you tick <b>Close batch</b>.</DialogDescription>
        </DialogHeader>
        <F label="Notes (optional)" className="block mt-1">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. worker returned everything for the day" data-testid="fab-receive-notes" />
        </F>
        <div className="border border-gray-200 rounded-md overflow-hidden mt-3">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Issued RM</th>
                <th className="px-3 py-2 text-right w-20">Issued</th>
                <th className="px-3 py-2 text-right w-24">Already Recv</th>
                <th className="px-3 py-2 w-60">Next-Level RM (output)</th>
                <th className="px-3 py-2 text-right w-28">Receive Qty</th>
                <th className="px-3 py-2 text-right w-28">Payable</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((l) => {
                const already = l.received_qty || 0;
                const rate = Number(chargeMap[l.raw_material_id] || 0);
                const nowQty = Number(rowInputs[l.id]?.qty) || 0;
                const rowPayable = nowQty * rate;
                const outVal = rowInputs[l.id]?.output_rm_id ?? l.raw_material_id;
                const outOptions = rmOptions.find((o) => o.value === outVal) ? rmOptions : [{ value: l.raw_material_id, label: l.raw_material_name, sublabel: l.unit || "" }, ...rmOptions];
                return (
                  <tr key={l.id}>
                    <td className="px-3 py-2 font-medium text-xs">
                      {l.raw_material_name} <span className="text-[10px] text-gray-400">{l.unit}</span>
                      {rate > 0 && <div className="text-[10px] text-gray-500 mt-0.5">Rate: {formatINR(rate)}/{l.unit || "unit"}</div>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{l.issued_qty}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-emerald-700">{already}</td>
                    <td className="px-3 py-2">
                      <ComboBoxSearch
                        value={outVal}
                        onChange={(v) => {
                          const opt = outOptions.find((o) => o.value === v);
                          setLine(l.id, { output_rm_id: v, output_rm_name: opt?.label });
                        }}
                        options={outOptions}
                        onSearchChange={setRmSearch}
                        placeholder="Pick output RM…"
                        testId={`fab-out-${l.id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" step="0.001" min="0" value={rowInputs[l.id]?.qty || ""}
                        onChange={(e) => setLine(l.id, { qty: e.target.value })}
                        className="text-right tabular-nums h-8" data-testid={`fab-rcv-${l.id}`} />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold text-[#9B1C1C]">
                      {rowPayable > 0 ? formatINR(rowPayable) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200">
                <td colSpan={5} className="px-3 py-2 text-right text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Total Payable This Receive</td>
                <td className="px-3 py-2 text-right tabular-nums font-bold text-[#9B1C1C]" data-testid="fab-total-payable">{formatINR(runningPayable)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="mt-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none" htmlFor="fab-close-batch">
            <input
              id="fab-close-batch"
              type="checkbox"
              checked={closeBatch}
              onChange={(e) => setCloseBatch(e.target.checked)}
              className="accent-[#9B1C1C]"
              data-testid="fab-close-batch"
            />
            <span>Close batch (mark completed — no further receives allowed)</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 gap-1.5 text-white" onClick={submit} disabled={saving} data-testid="fab-receive-submit">
            <CheckCircle2 className="h-4 w-4" /> {saving ? "Saving…" : "Confirm Receive"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, className = "", children }) {
  return <label className={`text-sm ${className}`}><span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">{label}</span>{children}</label>;
}
